import React, { useEffect, useState } from "react";
import AuthLayout from "../../layouts/AuthLayout";
import ForgotPasswordForm from "../../components/forms/ForgotPasswordForm";
import { useForm } from '@mantine/form';
import { forgotPassword, updatePassword, verifyOTP  } from "../../services/auth";
import ResetPasswordForm from "../../components/forms/ResetPasswordForm";
import { useNavigate } from "react-router-dom";
import { useToast } from '@chakra-ui/react'
import OTPForm from "../../components/forms/OTPForm";
import { OTPVerified } from "../../components/alert/OTPVerified";
import { ResetSuccess } from "../../components/alert/ResetSuccess";
import { ErrorToast } from "../../components/alert/ErrorToast";

export type PasswordType = { password: string }
export type EmailType = { email: string }
export type OTPType = { code: string }

export default function ForgotPassword() {
  const [count, setCount] = useState<number>(0)
  const [isLoading, setIsLoading] = useState({
    forgotPassword: false,
    resetPassword: false,
    otp: false,
    resendOtp: false
  })
  const [step, setStep] = useState('forgot_password')
  const toast = useToast()
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Forgot Password";
  }, []);

  const forgotPasswordForm = useForm({
    initialValues: { email: '' },

    validate: {
      email: (value) => (
        !value ? 'Email is required' :
        !/^\S+@\S+$/.test(value) ? 'Invalid email' : null
      )
    },
  });

  const otpForm = useForm({
    initialValues: { code: '' },

    validate: {
      code: (value) => (
        !value ? 'Enter OTP code' :
        value.length < 4 ? 'Enter 4 digit code' : null
      )
    },
  });

  const resetPasswordForm = useForm({
    initialValues: { password: '' },

    validate: {
      password: (value) => (
        !value ? 'Password is required' :
        value.length < 6 ? 'Password is less than 6 characters' : null
      )
    },
  });

  const handleForgotPassword = async (values: EmailType) => {
    setIsLoading({
      ...isLoading,
      forgotPassword: true
    })

    try {
      await forgotPassword(values)
      setStep('enter_otp')
      setIsLoading({
        ...isLoading,
        forgotPassword: false
      })
    } catch (error: any) {
      setIsLoading({
        ...isLoading,
        forgotPassword: false
      })

      forgotPasswordForm.setErrors({
        email: error.response.data.message
      })
    }
  }

  const handleResendOtp = async () => {
    setIsLoading({
      ...isLoading,
      resendOtp: true
    })

    try {
      await forgotPassword({ email: forgotPasswordForm.values.email })
      setCount(count + 1)
      setIsLoading({
        ...isLoading,
        resendOtp: false
      })
    } catch (error: any) {
      setIsLoading({
        ...isLoading,
        resendOtp: false
      })

      toast({
        title: "Failed to resend OTP",
        status: 'error',
        isClosable: true,
        duration: 2000,
        render: () => (
          <ErrorToast 
            message='Failed to resend OTP' 
          />
        )
      })
    }
  }

  const handleResetPassword = async (values: PasswordType) => {
    setIsLoading({
      ...isLoading,
      resetPassword: true
    })

    try {
      const payload = {
        password: values.password,
        email: forgotPasswordForm.values.email
      }

      await updatePassword(payload)
      toast({
        title: "Password reset successfull!",
        status: 'success',
        isClosable: true,
        duration: 2000,
        render: () => <ResetSuccess />
      })

      setTimeout(() => {
        setIsLoading({
          ...isLoading,
          resetPassword: false
        })

        navigate('/signin')
      }, 2000)
    } catch (error: any) {
      setIsLoading({
        ...isLoading,
        resetPassword: false
      })

      resetPasswordForm.setErrors({
        password: error.response.data.message
      })
    }
  }

  const handleOTP = async (values: OTPType) => {
    setIsLoading({
      ...isLoading,
      otp: true
    })

    try {
      const payload = {
        code: Number(values.code),
        email: forgotPasswordForm.values.email
      }

      await verifyOTP(payload)
      toast({
        title: "OTP verified!",
        status: 'success',
        isClosable: true,
        duration: 2000,
        render: () => <OTPVerified />
      })

      setTimeout(() => {
        setIsLoading({
          ...isLoading,
          otp: false
        })
        setStep('reset_password')
      }, 2000)
    } catch (error: any) {
      setIsLoading({
        ...isLoading,
        resetPassword: false
      })

      otpForm.setErrors({
        code: error.response.data.message
      })
    }
  }

  return (
    <React.Fragment>
      <AuthLayout>
        {step === 'forgot_password' &&
          <ForgotPasswordForm
            isLoading={isLoading}
            handleForgotPassword={handleForgotPassword}
            forgotPasswordForm={forgotPasswordForm}
          />
        }

        {step === 'enter_otp' &&
          <OTPForm 
            handleOTP={handleOTP}
            isLoading={isLoading}
            otpForm={otpForm}
            handleResendOtp={handleResendOtp}
            count={count}
          />
        }

        {step === 'reset_password' &&
          <ResetPasswordForm 
            isLoading={isLoading}
            handleResetPassword={handleResetPassword}
            resetPasswordForm={resetPasswordForm}
          />
        }
      </AuthLayout>
    </React.Fragment>
  )
}